import dayjs from 'dayjs';
import React from 'react';
import { useSelector } from 'react-redux';

import { L10nDate, L10nDistance } from '@travel/i18n';
import { ValueSize } from '@travel/i18n/src/components/L10nDistance';
import { Checkmark, InfoOutline, Login, Logout, TranslateBadge } from '@travel/icons/ui';
import RakutenAwardPopup from '@travel/traveler-core/components/RakutenAwardPopup';
import { MOBILE_SCREEN, useDeviceType, useDialogHandler } from '@travel/traveler-core/hooks';
import { Alert, RakutenAward } from '@travel/ui';
import type { IconTextItem } from '@travel/ui/components/IconTextList';
import { cx, isEmptyArray, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import { mappedIconTextListData } from 'pages/ProviderInformationPage/helpers';
import type { URLParams } from 'pages/ProviderInformationPage/resolver';

import {
  getDetailSummary,
  getIsDirectContractType,
  getIsFetching,
  getItem,
} from 'store/providerInformation/selectors';

import { PROVIDER_CREDIT_CARD_ICONS } from 'constants/creditCards';
import { EMPTY_ARRAY } from 'constants/defaultValue';
import { Translate } from 'core/translate';

import CategoryNames from './CategoryNames';
import CovidInformation from './CovidInformation';
import FacilityFeaturesBox from './FacilityFeaturesBox';
import FaxInfo from './FaxInfo';
import GoogleTranslate from './GoogleTranslate';
import LocationDetail from './LocationDetail';
import PaymentMethods from './PaymentMethods';
import PhoneInfo from './PhoneInfo';
import PropertyDescription from './PropertyDescription';
import TotalRoomNumber from './TotalRoomNumber';

import styles from './basicInformation.module.scss';

export type Props = {
  className?: string;
  /** Object representing the page identifiers */
  params: URLParams;
  /** parent page name for RAT popups */
  pageName: 'hotel:rooms_and_plans_dated_list' | 'hotel:rooms_and_plans_undated_list';
};

function BasicInformation(props: Props) {
  const provider = useSelector(getItem);
  const isFetching = useSelector(getIsFetching);
  const categoryNames = useSelector(getDetailSummary(props.params.pageIdentifier))?.categoryNames;
  const awards = useSelector(getDetailSummary(props.params.pageIdentifier))?.awards;
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const iconSize = 24;
  const isDirectContractType = useSelector(getIsDirectContractType);
  const facilityGroups = provider.facilities?.reduce(
    // @ts-ignore
    (result: Array<{ group: string; features: Array<IconTextItem> }>, facility) => {
      const features = mappedIconTextListData(facility.features, styles.featureIcon);
      if (isEmptyArray(features)) {
        return result;
      }

      return [
        ...result,
        {
          group: facility.group,
          features: mappedIconTextListData(facility.features, styles.featureIcon),
        },
      ];
    },
    [],
  );

  const supportedLangs = provider.languages?.map((lang) => ({
    text: lang,
    icon: <Checkmark size={iconSize} />,
  }));
  const payments = provider.payments?.map((item) => {
    const Icon = PROVIDER_CREDIT_CARD_ICONS[item.management] || null;
    const size = isMobile ? iconSize : 32;
    return {
      text: item.name,
      icon: Icon ? <Icon size={size} /> : <Checkmark size={size} />,
    };
  });

  const { checkInTimeStart, checkInTimeEnd, checkOutTime } = provider;
  const [checkInTimeStartH, checkInTimeStartM] = checkInTimeStart?.split(':') || EMPTY_ARRAY;
  const [checkInTimeEndH, checkInTimeEndM] = checkInTimeEnd?.split(':') || EMPTY_ARRAY;
  const [checkOutTimeH, checkOutTimeM] = checkOutTime?.split(':') || EMPTY_ARRAY;
  const isDateMissingPartial = !checkInTimeStart || !checkInTimeEnd || !checkOutTime;
  const isDateMissing = !checkInTimeStart && !checkInTimeEnd && !checkOutTime;
  const isCheckInTimeMissing = !checkInTimeStart && !checkInTimeEnd;
  const isCheckInTimeAvailable = checkInTimeStart && checkInTimeEnd;
  const { isOpen, onOpen, onClose } = useDialogHandler(false);
  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onOpen();
  };
  const renderTime = (hrs: string, min: string): string | JSX.Element => {
    if (!hrs || !min) {
      return '';
    }
    const formattedTime = dayjs().hour(Number(hrs)).minute(Number(min)).format();
    return <L10nDate value={formattedTime} format="LT" />;
  };

  const checkInCheckOutAlert = (
    <Alert
      className={styles.alertWrapper}
      titleWrapperClassName={styles.labeltitle}
      data-testid="checkInCheckOutAlert"
      title={
        <Translate id="Provider_Info.Out_To_Out.Basic_Info.Hotel_Info.Check_in_out_guidance" />
      }
      titleClassName={styles.alertTitle}
      isValidation={false}
      isClosable={false}
      iconClassName={styles.alertIcon}
    />
  );

  return (
    <div data-testid="basicInformation-wrapper" className={cx(styles.wrapper, props.className)}>
      <WithSkeletonLoading
        className={styles.container}
        isLoading={isFetching}
        row={5}
        count={3}
        customStyle="text"
      >
        <div className={cx(styles.infoContainer)}>
          <Translate id="Provider_Info.Basic_Info.Hotel_Info" className={styles.title} />
          <LocationDetail
            hideLocation={!isMobile}
            className={styles.locationContainer}
            pageName={props.pageName}
            zoomLevel={isMobile ? 15 : 17}
          />

          {isMobile && (
            <div className={styles.propertyInformationDetailsSP}>
              <CategoryNames categoryNames={categoryNames} />

              <PropertyDescription description={provider.description} />

              {!isDateMissing && (
                <div className={cx(styles.rowItem, styles.checkInDetails)}>
                  {!isCheckInTimeMissing && (
                    <div className={styles.leftContent}>
                      <div>
                        <Translate id="Provider_Info.Basic_Info.Hotel_Info.Check_in" />
                      </div>
                      <div>
                        {!checkInTimeEnd && (
                          <Translate
                            id="Common_Plan_Details.Out_To_Out.Reservation_Details.Check_In_Out.From"
                            data={{ time: renderTime(checkInTimeStartH, checkInTimeStartM) }}
                          />
                        )}
                        {isCheckInTimeAvailable && (
                          <>{renderTime(checkInTimeStartH, checkInTimeStartM)} &nbsp; ~ &nbsp;</>
                        )}
                        {!checkInTimeStart && (
                          <Translate
                            id="Common_Plan_Details.Out_To_Out.Reservation_Details.Check_In_Out.Until"
                            data={{ time: renderTime(checkInTimeEndH, checkInTimeEndM) }}
                          />
                        )}
                        {isCheckInTimeAvailable && renderTime(checkInTimeEndH, checkInTimeEndM)}
                      </div>
                    </div>
                  )}
                  {checkOutTime && (
                    <div className={styles.rightContent}>
                      <div>
                        <Translate id="Provider_Info.Basic_Info.Hotel_Info.Check_out" />
                      </div>
                      {!isDirectContractType && (
                        <Translate
                          id="Common_Plan_Details.Out_To_Out.Reservation_Details.Check_In_Out.Until"
                          data={{ time: renderTime(checkOutTimeH, checkOutTimeM) }}
                        />
                      )}
                      {isDirectContractType && (
                        <div>{renderTime(checkOutTimeH, checkOutTimeM)}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isDateMissingPartial && (
                <>
                  {isDateMissing && (
                    <Translate
                      id="Provider_Info.Out_To_Out.Basic_Info.Hotel_Info.Check_in_out_unavailable"
                      className={styles.checkInOutAlertTitle}
                    />
                  )}
                  <div className={styles.checkInOutAlertLabelSP}>{checkInCheckOutAlert} </div>
                </>
              )}

              {isNotEmptyArray(payments) && isDirectContractType && (
                <div className={styles.paymentDetails}>
                  <p className={styles.paymentTitle}>
                    <Translate id="Provider_Info.Basic_Info.Hotel_Info.Features.Payment" />
                  </p>
                  <PaymentMethods payments={payments} />
                </div>
              )}

              <PhoneInfo provider={provider} isMobile={isMobile} />
              {isDirectContractType && (
                <>
                  <FaxInfo provider={provider} isMobile={isMobile} />
                  <TotalRoomNumber provider={provider} isMobile={isMobile} />
                </>
              )}
              <GoogleTranslate provider={provider} />
            </div>
          )}

          {!isMobile && (
            <div
              className={cx(
                styles.propertyInformationDetails,
                !isDirectContractType && styles.block,
              )}
            >
              {!isDateMissing && (
                <>
                  <div
                    className={cx(
                      styles.checkInDetails,
                      !isDirectContractType && styles.checkInDetailsO2O,
                      isDateMissing && styles.noBorder,
                    )}
                  >
                    <div
                      className={cx(
                        styles.checkInOutWrapper,
                        !isDirectContractType && styles.noBorderBottom,
                      )}
                    >
                      {!isCheckInTimeMissing && (
                        <div className={cx(styles.checkIn, !checkOutTime && styles.noBorderRight)}>
                          <span className={styles.checkInOutTitle}>
                            <Translate id="Provider_Info.Basic_Info.Hotel_Info.Check_in" />:
                          </span>
                          <div className={styles.iconWithText}>
                            <Login size={21} />
                            {!checkInTimeEnd && (
                              <Translate
                                id="Common_Plan_Details.Out_To_Out.Reservation_Details.Check_In_Out.From"
                                data={{ time: renderTime(checkInTimeStartH, checkInTimeStartM) }}
                              />
                            )}
                            {isCheckInTimeAvailable && (
                              <>
                                {renderTime(checkInTimeStartH, checkInTimeStartM)} &nbsp; ~ &nbsp;
                              </>
                            )}
                            {!checkInTimeStart && (
                              <Translate
                                id="Common_Plan_Details.Out_To_Out.Reservation_Details.Check_In_Out.Until"
                                data={{ time: renderTime(checkInTimeEndH, checkInTimeEndM) }}
                              />
                            )}
                            {isCheckInTimeAvailable && renderTime(checkInTimeEndH, checkInTimeEndM)}
                          </div>
                        </div>
                      )}
                      {checkOutTime && (
                        <div className={styles.checkOut}>
                          <span className={styles.checkInOutTitle}>
                            <Translate id="Provider_Info.Basic_Info.Hotel_Info.Check_out" />:
                          </span>
                          <div className={styles.iconWithText}>
                            <Logout size={21} />
                            {!isDirectContractType && (
                              <Translate
                                id="Common_Plan_Details.Out_To_Out.Reservation_Details.Check_In_Out.Until"
                                data={{ time: renderTime(checkOutTimeH, checkOutTimeM) }}
                              />
                            )}
                            {isDirectContractType && (
                              <div>{renderTime(checkOutTimeH, checkOutTimeM)}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {isNotEmptyArray(payments) && (
                      <div className={styles.paymentDetails}>
                        <p className={styles.paymentTitle}>
                          <Translate id="Provider_Info.Basic_Info.Hotel_Info.Features.Payment" />
                        </p>
                        <PaymentMethods payments={payments} />
                      </div>
                    )}
                    {isDateMissingPartial && (
                      <div
                        className={cx(
                          styles.checkInOutAlertLabel,
                          (isCheckInTimeMissing || !checkOutTime) && styles.noBorderTop,
                        )}
                      >
                        {checkInCheckOutAlert}{' '}
                      </div>
                    )}
                  </div>
                </>
              )}
              {isDateMissing && (
                <div className={styles.checkInOutAlertWrapper}>
                  <Translate
                    id="Provider_Info.Out_To_Out.Basic_Info.Hotel_Info.Check_in_out_unavailable"
                    className={styles.checkInOutAlertTitle}
                  />
                  <div className={styles.checkInOutAlertLabel}>{checkInCheckOutAlert} </div>
                </div>
              )}
              <div className={styles.propertyDetails}>
                <CategoryNames categoryNames={categoryNames} />
                <PropertyDescription description={provider.description} />
                <LocationDetail
                  hideMap={true}
                  className={styles.locationContainer}
                  pageName={props.pageName}
                />

                <PhoneInfo provider={provider} isMobile={isMobile} />
                {isDirectContractType && (
                  <>
                    <FaxInfo provider={provider} isMobile={isMobile} />
                    <TotalRoomNumber provider={provider} isMobile={isMobile} />
                  </>
                )}
                <GoogleTranslate provider={provider} />
              </div>
            </div>
          )}
        </div>

        {/* Covid-19 */}
        <CovidInformation className={cx(styles.infoContainer, styles.container)} />

        {/* Rakuten Award */}
        {isOpen && (
          <div className={styles.japanStandardBtn}>
            <RakutenAwardPopup isOpen={isOpen} onClose={onClose} />
            <DataLayer
              // not using props.pageName per PDM request: https://jira.rakuten-it.com/jira/browse/TIDP-29840
              pageName="hotel:general_info:rakuten_award_information_popup"
              pageType="shop_item"
              siteSection="hotel"
            />
          </div>
        )}
        {isNotEmptyArray(awards) && (
          <div className={styles.container}>
            <button onClick={handleOpen} className={cx(styles.awardHeader, styles.bottomPadding)}>
              <Translate id="Provider_Info.Basic_Info.Award_Title" className={styles.subTitle} />
              <InfoOutline size={16} className={styles.awardInfo} />
            </button>
            <div className={styles.awardSection} data-testid="basicInformation-awardSection">
              {awards
                .filter((award) => award.image)
                .map((award) => (
                  <RakutenAward
                    key={award?.name}
                    title={award?.name}
                    image={award?.image}
                    className={styles.featureSize}
                  />
                ))}
            </div>
          </div>
        )}
        {/* facilities */}
        <div className={cx(styles.infoContainer, styles.container)}>
          <h3>
            <Translate
              id="Provider_Info.Basic_Info.Hotel_Info.Features.Title"
              className={styles.subTitle}
            />
          </h3>
          {isNotEmptyArray(facilityGroups) &&
            facilityGroups.map((facilityGroup) => {
              return (
                <FacilityFeaturesBox
                  id={facilityGroup.group}
                  key={facilityGroup.group}
                  title={facilityGroup.group}
                  items={facilityGroup.features}
                ></FacilityFeaturesBox>
              );
            })}
          {isNotEmptyArray(supportedLangs) && (
            <FacilityFeaturesBox
              id="facilitiy-language"
              key="facilitiy-language"
              title={<Translate id="Provider_Info.Basic_Info.Hotel_Info.Features.Language" />}
              items={supportedLangs}
            ></FacilityFeaturesBox>
          )}
        </div>
        {/* Remarks */}
        {isNotEmptyArray(provider.remarks) && (
          <div className={cx(styles.infoContainer, styles.container)}>
            <h3>
              <Translate
                id="Provider_Info.Basic_Info.Hotel_Info.Remarks"
                className={styles.subTitle}
              />
            </h3>
            {!isDirectContractType && (
              <p>
                <Translate id="Common_Plan_Details.Out_To_Out.Bed_Type_Special_Request_Disclaimer" />
              </p>
            )}
            {provider.remarks.map((remark) => (
              <p key={remark}>{remark}</p>
            ))}
            {provider?.isRemarksGoogleTranslated && (
              <p>
                <TranslateBadge data-testid="basicInformation-providerRemarks-translateBadge" />
              </p>
            )}
          </div>
        )}

        {/* Recommended spots */}
        {isNotEmptyArray(provider.spots) && (
          <div
            className={cx(styles.infoContainer, styles.container)}
            data-testid="providerInformation-basicInformation-recommendedSpots"
          >
            <h3>
              <Translate
                id="Provider_Info.Basic_Info.Hotel_Info.Spots"
                className={styles.subTitle}
              />
            </h3>
            <div className={styles.infoBox}>
              {provider.spots.map((spot) => (
                <p key={spot.location}>
                  <Translate
                    id="Provider_Info.Basic_Info.Distance"
                    data={{
                      distance: (
                        <L10nDistance
                          value={spot.value}
                          decimals={1}
                          valueSize={ValueSize.AUTO}
                          applyDecimalsTo={[ValueSize.LARGE]}
                        />
                      ),
                      nearby_terminal: spot.location,
                    }}
                  />
                </p>
              ))}
            </div>
          </div>
        )}
      </WithSkeletonLoading>
    </div>
  );
}

export default BasicInformation;
